import { Link } from "wouter";
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin, ArrowRight } from "lucide-react";
import { SITE_NAME, SITE_PHONE, SITE_EMAIL, SITE_ORIGIN, FOOTER_LOGO_URL, services, industries, getIndustryHref } from "@/lib/siteData";

export default function Footer() {
  const year = new Date().getFullYear();

  const navigationLinks = [
    { label: "Services", href: "/services" },
    { label: "Industries", href: "/industries" },
    { label: "Case Studies", href: "/case-studies" },
    { label: "Blog", href: "/blog" },
    { label: "About", href: "/about" },
    { label: "Contact", href: "/contact" },
  ];

  // Show first 6 non-AEO services and first 6 industries to match reference layout
  const footerServices = services.filter((s) => !s.slug.startsWith("aeo-agency/")).slice(0, 6);
  const aeoSubServices = [
    { title: "ChatGPT SEO", slug: "aeo-agency/chatgpt-seo" },
    { title: "Claude SEO", slug: "aeo-agency/claude-seo" },
    { title: "Gemini SEO", slug: "aeo-agency/gemini-seo" },
    { title: "Perplexity SEO", slug: "aeo-agency/perplexity-seo" },
    { title: "LLM SEO", slug: "aeo-agency/llm-seo" },
    { title: "GEO (Generative Engine)", slug: "aeo-agency/geo-seo" },
    { title: "AI Search Optimization", slug: "aeo-agency/aio-seo" },
  ];
  const footerIndustries = industries.slice(0, 6);

  return (
    <footer className="bg-navy-dark text-white" role="contentinfo" aria-label="Site footer">

      {/* CTA Banner — dark navy with gold button (matches reference) */}
      <div className="bg-navy border-b border-white/10">
        <div className="container py-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
              <Phone className="w-7 h-7 text-gold" aria-hidden="true" />
            </div>
            <div>
              <h2 className="font-display text-2xl font-bold text-white">Ready to Rank Higher Locally?</h2>
              <p className="text-white/70 mt-0.5 text-sm">Let's create a custom plan to get your business found by more local customers.</p>
            </div>
          </div>
          <a
            href="https://calendar.app.google/Pe1TK65N3x3CAeHJ9"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 inline-flex items-center gap-2 px-7 py-3.5 bg-gold text-navy font-bold rounded-md hover:bg-gold-light transition-all duration-200 active:scale-[0.97] text-sm uppercase tracking-wide"
          >
            Book Your Strategy Call <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </a>
        </div>
      </div>

      {/* Main footer — 5 columns matching reference */}
      <div className="container py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">

          {/* Column 1: Brand + Social */}
          <div className="lg:col-span-1">
            <Link href="/" aria-label="Local Ranking Coach - Home">
              <img
                src={FOOTER_LOGO_URL}
                alt="Local Ranking Coach"
                className="h-20 w-auto mb-5 max-w-[220px] object-contain"
                width="200"
                height="64"
                loading="lazy"
              />
            </Link>
            <p className="text-white/60 text-xs leading-relaxed mb-5">
              Boosting Trades to the Top
            </p>
            {/* Social icons */}
            <div className="flex items-center gap-3">
              <a
                href="https://www.facebook.com/localrankingcoach"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:text-gold hover:border-gold transition-colors"
              >
                <Facebook className="w-4 h-4" aria-hidden="true" />
              </a>
              <a
                href="https://www.instagram.com/localrankingcoach"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:text-gold hover:border-gold transition-colors"
              >
                <Instagram className="w-4 h-4" aria-hidden="true" />
              </a>
              <a
                href="https://www.linkedin.com/company/localrankingcoach"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:text-gold hover:border-gold transition-colors"
              >
                <Linkedin className="w-4 h-4" aria-hidden="true" />
              </a>
            </div>
          </div>

          {/* Column 2: Navigation */}
          <div>
            <p className="text-gold text-xs font-bold uppercase tracking-widest mb-5">Navigation</p>
            <ul className="flex flex-col gap-2.5" role="list">
              {navigationLinks.map((l) => (
                <li key={l.href}>
                  <Link
                    href={l.href}
                    className="text-white/70 hover:text-gold text-sm transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Services */}
          <div>
            <p className="text-gold text-xs font-bold uppercase tracking-widest mb-5">Services</p>
            <ul className="flex flex-col gap-2.5" role="list">
              {footerServices.map((s) => (
                <li key={s.slug}>
                  <Link
                    href={`/services/${s.slug}`}
                    className="text-white/70 hover:text-gold text-sm transition-colors"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
            {/* AEO Agency sub-services */}
            <div className="mt-6 pt-5 border-t border-white/10">
              <Link
                href="/services/aeo-agency"
                className="text-gold text-xs font-bold uppercase tracking-widest mb-3 block hover:text-gold-light transition-colors"
              >
                AEO Agency ›
              </Link>
              <ul className="flex flex-col gap-2" role="list">
                {aeoSubServices.map((s) => (
                  <li key={s.slug}>
                    <Link
                      href={`/services/${s.slug}`}
                      className="text-white/60 hover:text-gold text-xs transition-colors pl-2 border-l border-white/10 hover:border-gold"
                    >
                      {s.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Column 4: Industries */}
          <div>
            <p className="text-gold text-xs font-bold uppercase tracking-widest mb-5">Industries</p>
            <ul className="flex flex-col gap-2.5" role="list">
              {footerIndustries.map((ind) => (
                <li key={ind.slug}>
                  <a
                    href={getIndustryHref(ind.slug)}
                    className="text-white/70 hover:text-gold text-sm transition-colors"
                  >
                    {ind.title} SEO
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 5: Contact Us */}
          <div>
            <p className="text-gold text-xs font-bold uppercase tracking-widest mb-5">Contact Us</p>
            <div className="flex flex-col gap-4">
              <a
                href={`tel:${SITE_PHONE}`}
                className="flex items-start gap-3 text-white/70 hover:text-gold transition-colors group"
                aria-label={`Call ${SITE_PHONE}`}
              >
                <Phone className="w-4 h-4 flex-shrink-0 mt-0.5 text-gold" aria-hidden="true" />
                <span className="text-sm">{SITE_PHONE}</span>
              </a>
              <a
                href={`mailto:${SITE_EMAIL}`}
                className="flex items-start gap-3 text-white/70 hover:text-gold transition-colors group"
                aria-label={`Email ${SITE_EMAIL}`}
              >
                <Mail className="w-4 h-4 flex-shrink-0 mt-0.5 text-gold" aria-hidden="true" />
                <span className="text-sm">{SITE_EMAIL}</span>
              </a>
              <div className="flex items-start gap-3 text-white/70">
                <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5 text-gold" aria-hidden="true" />
                <address className="text-sm not-italic leading-relaxed">
                  Local Ranking Coach, Inc<br />
                  217 SE 1st Ave Ste 200 – 18<br />
                  Ocala, FL 34471
                </address>
              </div>
              <div className="flex flex-col gap-2 mt-2">
                <Link
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-gold text-navy font-bold text-xs rounded-md hover:bg-gold-light transition-colors uppercase tracking-wide"
                >
                  Contact Us
                </Link>
                <a
                  href={`tel:${SITE_PHONE}`}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-white/20 text-white font-bold text-xs rounded-md hover:border-gold hover:text-gold transition-colors uppercase tracking-wide"
                >
                  <Phone className="w-3.5 h-3.5" aria-hidden="true" />
                  Call Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="container py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-white/60 text-xs text-center sm:text-left">
            © {year} {SITE_NAME}. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link href="/privacy-policy" className="text-white/60 hover:text-gold text-xs transition-colors">Privacy Policy</Link>
            <Link href="/terms-of-service" className="text-white/60 hover:text-gold text-xs transition-colors">Terms of Service</Link>
            <Link href="/sitemap" className="text-white/60 hover:text-gold text-xs transition-colors">Sitemap</Link>
          </div>
        </div>
      </div>

      {/* LocalBusiness JSON-LD — physical office details (complements global Organization schema in index.html) */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": ["LocalBusiness", "ProfessionalService"],
            "@id": `${SITE_ORIGIN}/#localbusiness`,
            "name": "Local Ranking Coach, Inc",
            "alternateName": "Local Ranking Coach",
            "description": "Premier local SEO agency with 20+ years of experience helping trades and service businesses dominate local search nationwide.",
            "url": SITE_ORIGIN,
            "logo": {
              "@type": "ImageObject",
              "url": "https://localrankingcoach.com/images/logo.png",
              "width": 400,
              "height": 120
            },
            "image": "https://localrankingcoach.com/images/logo.png",
            "telephone": "+1-800-301-6220",
            "email": "hello@localrankingcoach.com",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "217 SE 1st Ave Ste 200-18",
              "addressLocality": "Ocala",
              "addressRegion": "FL",
              "postalCode": "34471",
              "addressCountry": "US"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 29.1872,
              "longitude": -82.1401
            },
            "openingHoursSpecification": [
              {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
                "opens": "08:00",
                "closes": "18:00"
              }
            ],
            "areaServed": {
              "@type": "Country",
              "name": "United States"
            },
            "serviceType": "Local SEO Agency",
            "slogan": "Boosting Trades to the Top",
            "foundingDate": "2004",
            "priceRange": "$$",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "5.0",
              "reviewCount": "200",
              "bestRating": "5",
              "worstRating": "1"
            },
            "sameAs": [
              "https://www.facebook.com/localrankingcoach",
              "https://www.linkedin.com/company/localrankingcoach",
              "https://www.instagram.com/localrankingcoach",
              "https://twitter.com/LocalRankCoach"
            ]
          })
        }}
      />
    </footer>
  );
}
